import { ActionContext } from 'vuex'
import sessionStrageUtils from '@/lib/sessionStrage/session-strage-utils'
import moment from 'moment'

import {
  getSearchFolderByKeyword,
  getListFolderByFileId,
  getListPrimaryFolder,
  postLinkChild,
} from '@/lib/api/cloudRepository'

// Stateの型定義
type StateType = {
  isEnabled: boolean
  columns: {
    key: string
    label: string
  }[]
  cloudDetailData: {
    id: number
    open: string
    name: string
    updateAt: string
    fileSize: string
    type: string
  }[]
  currentParentFolderId: string
  currentParentData: {
    id: string
    name: string
    active: boolean
  }[]
  currentData: {
    id: string
    name: string
    active: boolean
  }
  currentChildId: string
}

type MineTypes =
  | 'application/vnd.google-apps.folder'
  | 'text/plain'
  | 'image/svg+xml'

type CloudDetailDataType = {
  id: string
  mimeType: MineTypes
  modifiedTime: string
  name: string
  size?: string
  size_label?: string
  parents?: string[]
  downloadable: boolean
  webViewLink: string
}

type ParentData = {
  id?: string
  name?: string
  parent?: ParentData
}

const formatCloudData = (data: CloudDetailDataType[]) => {
  const formattedData = data.map((item: CloudDetailDataType) => {
    let fileType = ''
    if (item.mimeType === 'application/vnd.google-apps.folder') {
      fileType = 'folder'
    } else if (item.mimeType === 'text/plain') {
      fileType = 'file'
    } else if (item.mimeType.startsWith('image/')) {
      fileType = 'image'
    } else {
      fileType = 'file'
    }

    return {
      id: item.id,
      open: fileType === 'folder' ? 'open' : '',
      name: item.name,
      updateAt: moment(item.modifiedTime).format('YYYY/MM/DD HH:mm'),
      fileSize: item.size_label || '',
      type: fileType,
      downloadable: item.downloadable,
      webViewLink: item.webViewLink,
    }
  })

  return formattedData
}

const getCurrentParentFolderId = (
  fileList: CloudDetailDataType[]
): string | undefined => {
  const parentFile = fileList.find(
    (file: CloudDetailDataType) => file.parents && file.parents.length > 0
  )
  const currentParentFolderId =
    parentFile && parentFile.parents ? parentFile.parents[0] : ''

  return currentParentFolderId
}

const getCurrentParentFolderIdForFileid = (
  parentData: ParentData = {}
): { id: string; name: string; active: boolean }[] => {
  const parentFile: { id: string; name: string; active: boolean }[] = []

  if (!Object.keys(parentData).length) return []

  let current: ParentData | undefined = parentData

  while (current && current.id) {
    parentFile.push({
      id: current.id,
      name: current.name || '',
      active: true,
    })
    current = current.parent
  }

  return parentFile
}
// State
const state: StateType = {
  isEnabled: false,
  columns: [
    { key: 'open', label: '' },
    { key: 'name', label: '名前' },
    { key: 'updateAt', label: '最終更新' },
    { key: 'controls', label: '' },
  ],
  cloudDetailData: [],
  currentParentFolderId: '',
  currentParentData: [],
  currentData: {
    id: '',
    name: '',
    active: false,
  },
  currentChildId: '',
}

// Mutations
const mutations = {
  initState(state: StateType) {
    state.cloudDetailData = []
    state.currentParentFolderId = ''
    state.currentParentData = []
    state.currentData = {
      id: '',
      name: '',
      active: false,
    }
    state.currentChildId = ''
  },
  setEnabled(state: StateType, value: boolean) {
    state.isEnabled = value
  },
  setCloudData(state: StateType, data: StateType['cloudDetailData']) {
    state.cloudDetailData = data
  },
  setCurrentParentFolderId(state: StateType, id: string) {
    state.currentParentFolderId = id
  },
  setCurrentParentData(state: StateType, data: StateType['currentParentData']) {
    state.currentParentData = data
  },
  setCurrentData(state: StateType, data: StateType['currentData']) {
    state.currentData = data
  },
  setChildId(state: StateType, childId: string) {
    state.currentChildId = childId
  },
}

// Actions
type RootState = {}
type CloudActionContext = ActionContext<StateType, RootState>

const actions = {
  updateCurrentData(
    { commit }: CloudActionContext,
    data: StateType['currentData']
  ) {
    commit('setCurrentData', data)
  },

  async fetchCloudData(
    { state, commit }: CloudActionContext,
    { keyword = '' }: { keyword?: string }
  ) {
    try {
      const currentOffice = sessionStrageUtils.handleGetSessionStorage(
        'currentStaff'
      )
      const officeId = currentOffice.office_id
      const fileId = state.currentData.id
      const childId = state.currentChildId

      const res = await getSearchFolderByKeyword(
        officeId,
        fileId,
        keyword,
        childId
      )

      if (res?.data?.data?.current) {
        const currentData = {
          id: res.data.data.current.folder_id,
          name: res.data.data.current.folder_name,
          active: false,
        }
        commit('setCurrentData', currentData)
        commit('setChildId', res.data.data.current.child_id)
      }

      if (res?.data?.data?.files) {
        const formattedData = formatCloudData(res.data.data.files)
        const currentParentFolderId = getCurrentParentFolderId(
          res.data.data.files
        )

        const currentParentData = getCurrentParentFolderIdForFileid(
          res.data.data.parents
        )

        commit('setCloudData', formattedData)
        commit('setCurrentParentData', currentParentData)
        commit('setCurrentParentFolderId', currentParentFolderId)

        return Promise.resolve(res)
      } else {
        commit('initState')

        return Promise.reject(res)
      }
    } catch (error) {
      commit('initState')
      return Promise.reject()
    }
  },

  async fetchCloudDataForFileId(
    { commit }: CloudActionContext,
    fileId: string
  ) {
    try {
      commit('initState')

      const currentOffice = sessionStrageUtils.handleGetSessionStorage(
        'currentStaff'
      )
      const officeId = currentOffice.office_id

      const res = await getListFolderByFileId(officeId, fileId)

      if (res?.data?.data?.current) {
        const currentData = {
          id: res.data.data.current.folder_id,
          name: res.data.data.current.folder_name,
          active: false,
        }
        commit('setCurrentData', currentData)
        commit('setChildId', res.data.data.current.child_id)
      }

      if (res?.data?.data?.files) {
        const formattedData = formatCloudData(res.data.data.files)

        const currentParentData = getCurrentParentFolderIdForFileid(
          res.data.data.parents
        )

        commit('setCloudData', formattedData)
        commit('setCurrentParentData', currentParentData)
        commit('setCurrentParentFolderId', res.data.data.parents.id)

        return Promise.resolve(res)
      } else {
        commit('initState')

        return Promise.reject(res)
      }
    } catch (error) {
      commit('initState')
      return Promise.reject(error)
    }
  },

  async fetchCloudDataForPrimaryList(
    { commit }: CloudActionContext,
    { childId = '' }: { childId?: string } = {}
  ) {
    try {
      commit('initState')

      const currentOffice = sessionStrageUtils.handleGetSessionStorage(
        'currentStaff'
      )
      const officeId = currentOffice.office_id

      const res = await getListPrimaryFolder(officeId, childId)

      if (res?.data?.data?.current) {
        const currentData = {
          id: res.data.data.current.folder_id,
          name: res.data.data.current.folder_name,
          active: false,
        }
        commit('setCurrentData', currentData)
        commit('setChildId', res.data.data.current.child_id)
      }

      if (res?.data?.data?.files && res?.data?.data?.files.length) {
        const formattedData = formatCloudData(res.data.data.files)
        const currentParentFolderId = getCurrentParentFolderId(
          res.data.data.files
        )

        commit('setCloudData', formattedData)
        commit('setCurrentParentData', [])
        commit('setCurrentParentFolderId', currentParentFolderId)

        return Promise.resolve(res)
      } else {
        commit('setCloudData', [])
        commit('setCurrentParentData', [])
        commit('setCurrentParentFolderId', '')
        // MEMO: ファイルが存在しない場合、新規作成モーダルを出すために例外処理は行わずresolveする
        return Promise.resolve(res)
      }
    } catch (error) {
      commit('initState')
      return Promise.reject(error)
    }
  },

  async createFolderForChildId(
    { commit }: CloudActionContext,
    { childId }: { childId: string }
  ) {
    try {
      const currentOffice = sessionStrageUtils.handleGetSessionStorage(
        'currentStaff'
      )
      const officeId = currentOffice.office_id
      const staffId = currentOffice.staff_id

      const res = await postLinkChild(officeId, childId, staffId)

      return Promise.resolve(res)
    } catch (error) {
      commit('setCloudData', [])
      return Promise.reject()
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
